import {
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  PopoverOrigin,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import { CheckBox, CheckBoxOutlineBlank, Settings } from "@material-ui/icons";
import { ChangeEvent, MouseEventHandler, useEffect, useState } from "react";

const AnchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};

const TransformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};

interface Props {
  loading?: boolean;
  clusteringEnabled?: boolean;
  onSetClusteringEnabled?: (value: boolean) => void;
  /** Minutes after which driving is treated as off; 0 = filter disabled. Default 60. */
  ignitionStaleMinutes?: number;
  onIgnitionStaleMinutesChange?: (value: number | null) => void;
}

const DEFAULT_IGNITION_STALE_MINUTES = 60;

export const DashboardMapSettings = ({
  clusteringEnabled,
  onSetClusteringEnabled,
  ignitionStaleMinutes = DEFAULT_IGNITION_STALE_MINUTES,
  onIgnitionStaleMinutesChange,
  loading,
}: Props) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const menuOpen = Boolean(anchorEl);
  const [inputValue, setInputValue] = useState<string>(
    ignitionStaleMinutes === 0 ? "" : String(ignitionStaleMinutes)
  );

  // Sync local input when external value changes (e.g. on initial load)
  useEffect(() => {
    setInputValue(ignitionStaleMinutes === 0 ? "" : String(ignitionStaleMinutes));
  }, [ignitionStaleMinutes]);

  // Debounce propagation of changes to parent / localStorage / API refresh
  useEffect(() => {
    if (!onIgnitionStaleMinutesChange) {
      return;
    }

    const handle = setTimeout(() => {
      const raw = inputValue.trim();
      if (raw === "") {
        onIgnitionStaleMinutesChange(0);
        return;
      }
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n) && n >= 0) {
        onIgnitionStaleMinutesChange(n);
      }
    }, 800);

    return () => clearTimeout(handle);
  }, [inputValue, onIgnitionStaleMinutesChange]);

  const handleMenu: MouseEventHandler<HTMLButtonElement> = (event) =>
    setAnchorEl((x) => (x ? null : event.currentTarget));
  const handleClose = () => setAnchorEl(null);
  const handleClick = () => onSetClusteringEnabled?.(!clusteringEnabled);
  const handleIgnitionStaleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  if (loading) {
    return null;
  }

  return (
    <div className="dashboard-map-settings">
      <div id="dashboard-map-settings-timezone-info" />
      <Paper
        className="dashboard-map-settings-button"
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <IconButton onClick={handleMenu}>
          <Settings />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={AnchorOrigin}
          transformOrigin={TransformOrigin}
          getContentAnchorEl={null}
          open={menuOpen}
          onClose={handleClose}
        >
          <MenuItem onClick={handleClick}>
            <div className="dashboard-map-settings-menu-item">
              {clusteringEnabled ? <CheckBox /> : <CheckBoxOutlineBlank />}
              <Typography variant="body2">Enable clustering</Typography>
            </div>
          </MenuItem>
          <Divider />
          <MenuItem
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            disableRipple
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }} className="dashboard-map-settings-menu-item">
              <Typography variant="body2" style={{ marginRight: 8 }}>
                Stale driving threshold (minutes):
              </Typography>
              <TextField
                type="number"
                size="small"
                value={inputValue}
                onChange={handleIgnitionStaleChange}
                placeholder={"0 = disabled"}
                inputProps={{ min: 0, step: 1 }}
                variant="outlined"
                margin="dense"
                style={{ width: "auto" }}
              />
            </div>
          </MenuItem>
        </Menu>
      </Paper>
    </div>
  );
};
